import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Plus, Trash, AirplaneTilt, Clock, Coffee, CheckCircle } from "@phosphor-icons/react";

const ROLES_VEN_TODO = new Set(["admin", "gestor", "gestor_senior"]);
const DAYS = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

export default function CalendarSettings() {
  const { user } = useAuth();
  const canSeeAll = ROLES_VEN_TODO.has(user?.role);
  const isAdmin = user?.role === "admin";
  const [peritos, setPeritos] = useState([]);
  const [target, setTarget] = useState(user?.id);
  const [schedule, setSchedule] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (canSeeAll) api.get("/peritos").then((r) => setPeritos(r.data || [])).catch(() => {});
  }, [canSeeAll]);

  const load = async (id) => {
    try {
      const r = await api.get(`/calendar/schedule/${id}`);
      setSchedule(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  useEffect(() => { if (target) load(target); }, [target]);

  const setDay = (key, patch) => setSchedule((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const saveHours = async () => {
    setSaving(true);
    try {
      const body = {};
      DAYS.forEach((d) => { body[d.key] = schedule[d.key]; });
      await api.put(`/calendar/schedule/${target}`, body);
      toast.success("Horario guardado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const addVacation = async ({ start_date, end_date, label }) => {
    try {
      await api.post(`/calendar/schedule/${target}/vacations`, { start_date, end_date, label });
      toast.success(isAdmin ? "Vacaciones aprobadas" : "Vacaciones solicitadas (pendiente de aprobación)");
      load(target);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const removeVacation = async (id) => {
    if (!window.confirm("¿Borrar este periodo?")) return;
    await api.delete(`/calendar/schedule/${target}/vacations/${id}`);
    load(target);
  };
  const approveVacation = async (id) => {
    await api.post(`/calendar/schedule/${target}/vacations/${id}/approve`);
    toast.success("Aprobado");
    load(target);
  };

  if (!schedule) return <div className="p-6 text-sm text-muted">Cargando…</div>;

  return (
    <div className="max-w-4xl space-y-6" data-testid="cal-settings-page">
      <div className="flex items-center justify-between">
        <Link to="/calendar" className="text-xs text-muted hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" weight="bold" /> Volver al calendario
        </Link>
        {canSeeAll && peritos.length > 0 && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="text-xs border border-border rounded-md px-3 py-1.5 bg-white" data-testid="cal-settings-perito">
            {peritos.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      <h1 className="font-display font-black text-2xl tracking-tight">Configuración de la agenda</h1>

      {/* Horario semanal */}
      <section className="bg-white border border-border rounded-md">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-700" weight="duotone" />
          <h2 className="font-semibold text-sm">Horario laboral</h2>
        </div>
        <div className="p-5 space-y-2">
          {DAYS.map((d) => {
            const wh = schedule[d.key] || { enabled: false };
            return (
              <div key={d.key} className="grid grid-cols-1 md:grid-cols-[120px_60px_1fr_1fr_120px] gap-2 items-center">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <input type="checkbox" checked={!!wh.enabled} onChange={(e) => setDay(d.key, { enabled: e.target.checked })} data-testid={`cal-day-${d.key}-enabled`} />
                  {d.label}
                </label>
                {wh.enabled ? (
                  <>
                    <span className="text-[10px] text-muted">Inicio</span>
                    <input type="time" value={wh.start || "09:00"} onChange={(e) => setDay(d.key, { start: e.target.value })} className="text-xs border border-border rounded-md px-2 py-1.5" />
                    <input type="time" value={wh.end || "18:00"} onChange={(e) => setDay(d.key, { end: e.target.value })} className="text-xs border border-border rounded-md px-2 py-1.5" />
                    <span className="text-[10px] text-muted col-start-3 md:col-start-auto"></span>
                  </>
                ) : <div className="col-span-4 text-xs text-muted">No laborable</div>}
                {wh.enabled && (
                  <div className="md:col-span-5 grid grid-cols-[120px_60px_1fr_1fr_120px] gap-2 items-center text-[11px] text-muted ml-6">
                    <Coffee className="w-3 h-3 text-amber-700" weight="fill" />
                    <span>Comida</span>
                    <input type="time" value={wh.lunch_start || "14:00"} onChange={(e) => setDay(d.key, { lunch_start: e.target.value })} className="text-xs border border-border rounded-md px-2 py-1.5" />
                    <input type="time" value={wh.lunch_end || "15:00"} onChange={(e) => setDay(d.key, { lunch_end: e.target.value })} className="text-xs border border-border rounded-md px-2 py-1.5" />
                    <span></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-border flex justify-end">
          <button onClick={saveHours} disabled={saving} className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="cal-save-hours">
            <FloppyDisk className="w-3.5 h-3.5" weight="bold" />
            {saving ? "Guardando..." : "Guardar horario"}
          </button>
        </div>
      </section>

      {/* Vacaciones */}
      <section className="bg-white border border-border rounded-md">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <AirplaneTilt className="w-4 h-4 text-amber-700" weight="duotone" />
          <h2 className="font-semibold text-sm">Vacaciones y ausencias</h2>
        </div>
        <div className="p-5 space-y-3">
          <NewVacation onAdd={addVacation} isAdmin={isAdmin} />
          <div className="space-y-1.5">
            {(schedule.vacations || []).length === 0 ? (
              <p className="text-xs text-muted">Sin periodos registrados.</p>
            ) : schedule.vacations.map((v) => (
              <div key={v.id} className="border border-border rounded-md px-3 py-2 flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    {new Date(v.start_date).toLocaleDateString("es-ES")} → {new Date(v.end_date).toLocaleDateString("es-ES")}
                    {v.label && <span className="text-muted font-normal ml-2">· {v.label}</span>}
                  </div>
                  <div className="text-[11px] mt-0.5">
                    {v.status === "approved" && <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" weight="fill" /> Aprobado</span>}
                    {v.status === "requested" && <span className="text-amber-700">Pendiente de aprobación</span>}
                    {v.status === "rejected" && <span className="text-rose-700">Rechazado</span>}
                  </div>
                </div>
                {isAdmin && v.status === "requested" && (
                  <button onClick={() => approveVacation(v.id)} className="text-xs px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-md font-semibold">Aprobar</button>
                )}
                <button onClick={() => removeVacation(v.id)} className="text-muted hover:text-rose-600 p-1.5" aria-label="Borrar"><Trash className="w-3.5 h-3.5" weight="bold" /></button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function NewVacation({ onAdd, isAdmin }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [label, setLabel] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!start || !end) { toast.error("Indica fecha inicio y fin"); return; }
    onAdd({ start_date: start, end_date: end, label });
    setStart(""); setEnd(""); setLabel("");
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end border border-dashed border-border rounded-md p-3 bg-slate-50/40">
      <label className="block">
        <span className="text-[11px] font-semibold text-muted block mb-1">Inicio</span>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} required className="w-full text-xs border border-border rounded-md px-2 py-1.5" data-testid="cal-vac-start" />
      </label>
      <label className="block">
        <span className="text-[11px] font-semibold text-muted block mb-1">Fin</span>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} required className="w-full text-xs border border-border rounded-md px-2 py-1.5" data-testid="cal-vac-end" />
      </label>
      <label className="block">
        <span className="text-[11px] font-semibold text-muted block mb-1">Concepto (opcional)</span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Verano · Boda · Asuntos propios" className="w-full text-xs border border-border rounded-md px-2 py-1.5" />
      </label>
      <button type="submit" className="btn-brand text-xs px-3 py-2 rounded-md font-semibold inline-flex items-center gap-1.5" data-testid="cal-vac-add">
        <Plus className="w-3 h-3" weight="bold" />
        {isAdmin ? "Añadir y aprobar" : "Solicitar"}
      </button>
    </form>
  );
}
